import { useEffect, useMemo, useState } from 'react';
import { Search, FolderPlus, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getAllSavedLists, subscribeToSavedListsChanged } from '../../services/savedListService';
import { getSensitiveData } from '../../utils/secureStorage';
import type { SavedListDto } from '../../types/savedLists';
import { useFieldLabel } from '../../hooks/useFieldLabel';

const FILTERS = ['My Lists'] as const;

type FilterOption = typeof FILTERS[number];

export default function SavedListsPage() {
  const navigate = useNavigate();
  const [allLists, setAllLists] = useState<SavedListDto[]>([]);
  const [currentUserId, setCurrentUserId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterOption>('My Lists');
  const [isLoading, setIsLoading] = useState(false);
  const labels = useFieldLabel('MENU_PRIVATE_LIST', 'Private List');

  useEffect(() => {
    const loadLists = async () => {
      setIsLoading(true);
      const userId = (await getSensitiveData('userId')) || '';
      setCurrentUserId(userId);
      try {
        setAllLists(await getAllSavedLists());
      } finally {
        setIsLoading(false);
      }
    };

    loadLists();
    return subscribeToSavedListsChanged(loadLists);
  }, []);

  const visibleLists = useMemo(() => {
    const filtered = activeFilter === 'My Lists'
      ? allLists.filter((item) => item.createdByUserId === currentUserId)
      : allLists.filter((item) => item.createdByUserId !== currentUserId);

    return filtered.filter((item) =>
      item.listName.toLowerCase().includes(searchTerm.trim().toLowerCase())
    );
  }, [activeFilter, allLists, currentUserId, searchTerm]);

  const handleNavigateToFindPeople = () => navigate('/findPeople');

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="mb-0 max-w-2xl text-sm leading-6 text-gray-600 sm:text-base">
            Save talent pools of selected experts and reuse them for messaging, lists, exports, and staffing.
          </p>
        </div>
        <button
          onClick={handleNavigateToFindPeople}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700"
        >
          <FolderPlus className="h-4 w-4" />
          Create {labels}
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[240px_1fr]">
        <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-sm font-semibold text-gray-900">Filters</p>
          <div className="space-y-2">
            {FILTERS.map((filter) => (
              <button
                key={filter}
                type="button"
                onClick={() => setActiveFilter(filter)}
                className={`w-full rounded-lg px-4 py-3 text-left text-sm font-medium transition ${
                  activeFilter === filter
                    ? 'border border-blue-600 bg-blue-50 text-blue-700'
                    : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search lists by name..."
                className="w-full rounded-lg border border-gray-200 bg-white py-3 pl-11 pr-4 text-sm text-gray-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-600 shadow-sm">
              Loading {labels}...
            </div>
          ) : visibleLists.length > 0 ? (
            <div className="grid grid-cols-1 gap-4">
              {visibleLists.map((list) => (
                <div key={list.id} className="min-w-0 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <h2 className="text-xl font-bold text-gray-900 truncate" title={list.listName}>{list.listName}</h2>
                      {list.description ? <p className="mt-2 text-sm text-gray-600 break-words">{list.description}</p> : null}
                    </div>
                    <button
                      onClick={() => navigate(`/saved-lists/${list.id}`)}
                      className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
                    >
                      Open {labels}
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="mt-5 grid gap-3 sm:grid-cols-3">
                    <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-600">
                      <p className="font-semibold text-slate-900">{list.memberCount ?? list.memberUserIds.length} Members</p>
                    </div>
                    <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-600">
                      <p className="font-semibold text-slate-900">Created By</p>
                      <p>{list.createdByUserId === currentUserId ? 'You' : list.createdByName || list.createdByUserId}</p>
                    </div>
                    <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-600">
                      <p className="font-semibold text-slate-900">Created On</p>
                      <p>{new Date(list.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center text-gray-600 shadow-sm">
              <p className="text-lg font-semibold text-gray-900">No {labels} found.</p>
              <p className="mt-2 text-sm text-gray-600">Create your first {labels.toLowerCase()} from Find People to save talent pools.</p>
              <button
                onClick={handleNavigateToFindPeople}
                className="mt-6 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
              >
                Create first {labels.toLowerCase()}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
